import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL from relative or absolute path
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export const HomePage = () => {
  const { t, language, convertPrice } = useApp();
  const [heroImage, setHeroImage] = useState('');
  const [heroTitle, setHeroTitle] = useState('');
  const [featuredApartments, setFeaturedApartments] = useState([]);
  const [homeFeatures, setHomeFeatures] = useState([]);
  const [colorSettings, setColorSettings] = useState({
    status_available: '#22c55e',
    status_occupied: '#ef4444',
    status_available_soon: '#f59e0b',
    label_promo: '#facc15',
    label_recommended: '#3b82f6',
    label_new: '#10b981'
  });

  useEffect(() => {
    fetchSettings();
    fetchFeaturedApartments();
    fetchColorSettings();
  }, [language]);

  const fetchColorSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/colors`);
      setColorSettings(data);
    } catch (error) {
      console.log('Using default colors');
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchFeaturedApartments();
  }, [language]);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/global`);
      setHeroImage(
        data.cover_image_url ||
          'https://customer-assets.emergentagent.com/job_653c4008-d37f-4e28-b6cd-13daee6e37ad/artifacts/voe72jbl_palat.jpg'
      );
      // Set home features from settings or use defaults
      if (data.home_features && data.home_features.length > 0) {
        setHomeFeatures(data.home_features);
      } else {
        setHomeFeatures([
          { title: { ro: 'Locații Premium', en: 'Premium Locations', fr: 'Emplacements Premium' }, description: { ro: 'Apartamente în cele mai căutate zone din Iași', en: 'Apartments in the most sought-after areas of Iași', fr: 'Appartements dans les zones les plus recherchées de Iași' } },
          { title: { ro: 'Servicii Complete', en: 'Full Service', fr: 'Services Complets' }, description: { ro: 'Suport complet de la vizionare până la semnarea contractului', en: 'Complete support from viewing to contract signing', fr: 'Support complet de la visite à la signature du contrat' } },
          { title: { ro: 'Prețuri Transparente', en: 'Transparent Pricing', fr: 'Prix Transparents' }, description: { ro: 'Fără costuri ascunse sau surprize neplăcute', en: 'No hidden costs or unpleasant surprises', fr: 'Pas de frais cachés ou de surprises désagréables' } }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch settings');
      setHeroImage(
        'https://customer-assets.emergentagent.com/job_653c4008-d37f-4e28-b6cd-13daee6e37ad/artifacts/voe72jbl_palat.jpg'
      );
    }
  };

  const fetchFeaturedApartments = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments`);
      // Filter apartments with labels (promo, recommended, new) - show all statuses
      const featured = data.filter(
        (apt) => apt.labels && apt.labels.length > 0
      ).slice(0, 6);
      setFeaturedApartments(featured);
    } catch (error) {
      console.error('Failed to fetch featured apartments');
    }
  };

  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  // Helper to determine text color based on background brightness
  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const getStatusBadge = (status, available_in_months = 0) => {
    if (status === 'available') {
      return (
        <span 
          className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
          style={{ backgroundColor: colorSettings.status_available }}
        >
          {t('available')}
        </span>
      );
    }
    if (status === 'available_soon') {
      const monthText = available_in_months === 1 
        ? (language === 'ro' ? 'lună' : language === 'fr' ? 'mois' : 'month')
        : (language === 'ro' ? 'luni' : language === 'fr' ? 'mois' : 'months');
      return (
        <span 
          className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
          style={{ backgroundColor: colorSettings.status_available_soon }}
        >
          {language === 'ro' ? `Disponibil în ${available_in_months} ${monthText}` 
            : language === 'fr' ? `Disponible dans ${available_in_months} ${monthText}`
            : `Available in ${available_in_months} ${monthText}`}
        </span>
      );
    }
    return (
      <span 
        className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
        style={{ backgroundColor: colorSettings.status_occupied }}
      >
        {t('occupied')}
      </span>
    );
  };

  const getLabels = (labels) => {
    if (!labels || labels.length === 0) return null;
    const colorMap = {
      promo: colorSettings.label_promo,
      recommended: colorSettings.label_recommended,
      new: colorSettings.label_new,
    };
    return (
      <div className="flex flex-col gap-1">
        {labels.map((labelText, index) => {
          const bgColor = colorMap[labelText] || '#6b7280';
          const textColor = getContrastColor(bgColor);
          return (
            <span 
              key={index}
              className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded shadow-lg"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {labelText}
            </span>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (language === 'ro') {
      setHeroTitle('Apartamente de închiriat în Iași');
    } else if (language === 'fr') {
      setHeroTitle('Appartements à louer à Iași');
    } else {
      setHeroTitle('Apartments for Rent in Iași');
    }
  }, [language]);

  return (
    <div data-testid="homepage" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        />
        {/* Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(2,6,23,0.3), rgba(2,6,23,0.9))',
          }}
        />

        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1
                data-testid="hero-title"
                className="text-5xl md:text-7xl font-serif font-medium tracking-tight leading-none text-foreground mb-6"
              >
                {heroTitle}
              </h1>
              <p
                data-testid="hero-subtitle"
                className="text-lg md:text-xl leading-relaxed font-light text-muted-foreground mb-12 max-w-2xl mx-auto"
              >
                {t('hero_subtitle')}
              </p>
              <Link
                to="/apartments"
                data-testid="view-apartments-button"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 font-serif italic text-lg transition-all duration-300 group rounded-full"
              >
                {t('view_apartments')}
                <ArrowRight
                  className="transition-transform duration-300 group-hover:translate-x-2"
                  size={20}
                />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Apartments Section */}
      {featuredApartments.length > 0 && (
        <section className="py-24 bg-slate-900/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12 text-center"
            >
              <h2 className="text-4xl md:text-5xl font-serif mb-4">
                {language === 'ro'
                  ? 'Apartamente Recomandate'
                  : language === 'fr'
                    ? 'Appartements Recommandés'
                    : 'Featured Apartments'}
              </h2>
              <p className="text-lg text-muted-foreground">
                {language === 'ro'
                  ? 'Descoperă cele mai noi și atractive oferte'
                  : language === 'fr'
                    ? 'Découvrez les offres les plus récentes et attractives'
                    : 'Discover our newest and most attractive offers'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredApartments.map((apartment, index) => (
                <motion.div
                  key={apartment.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`featured-apartment-${apartment.id}`}
                  className="group relative overflow-hidden bg-card border border-border hover:border-primary/50 transition-all duration-500"
                >
                  {/* Image - Clickable */}
                  <Link to={`/apartments/${apartment.id}`} className="relative h-64 overflow-hidden block cursor-pointer">
                    <img
                      src={getImageUrl(apartment.images[0]) || 'https://via.placeholder.com/400x300?text=No+Image'}
                      alt={getTranslatedText(apartment.title)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Labels and Status - same as ApartmentsPage */}
                    <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                      {getLabels(apartment.labels)}
                      {getStatusBadge(apartment.status, apartment.available_in_months)}
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-6">
                    {/* Title - Clickable */}
                    <Link to={`/apartments/${apartment.id}`}>
                      <h3 className="text-2xl font-serif mb-2 group-hover:text-primary transition-colors cursor-pointer hover:text-primary">
                        {getTranslatedText(apartment.title)}
                      </h3>
                    </Link>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {getTranslatedText(apartment.description)}
                    </p>

                    {/* Price */}
                    <div className="mb-4">
                      <span className="text-2xl font-serif text-primary">
                        {convertPrice(apartment.price_eur, apartment.price_ron)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {language === 'ro' ? ' / lună' : language === 'fr' ? ' / mois' : ' / month'}
                      </span>
                    </div>

                    {/* View Details Button */}
                    <Link
                      to={`/apartments/${apartment.id}`}
                      data-testid={`view-featured-${apartment.id}`}
                      className="inline-block w-full border border-primary/50 text-primary hover:bg-primary/10 px-6 py-3 text-center text-sm uppercase tracking-wider transition-all duration-300 rounded-full"
                    >
                      {t('view_details')}
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* View All Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center mt-12"
            >
              <Link
                to="/apartments"
                data-testid="view-all-apartments"
                className="inline-flex items-center gap-3 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-4 font-serif text-lg transition-all duration-300 rounded-full"
              >
                {language === 'ro'
                  ? 'Vezi Toate Apartamentele'
                  : language === 'fr'
                    ? 'Voir Tous les Appartements'
                    : 'View All Apartments'}
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-24 bg-slate-950">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {homeFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="p-8 bg-slate-900/50 border border-white/5 hover:bg-slate-900/80 transition-all"
              >
                <h3 className="text-2xl md:text-3xl font-serif mb-4 text-primary">
                  {feature.title?.[language] || feature.title?.en || ''}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {feature.description?.[language] || feature.description?.en || ''}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;